import React, { useState, useEffect } from 'react';
import { AUTO_REFRESH_OPTIONS } from '../ticketConstants';
import { isStoreOpen } from '../ticketUtils';
import { syncTickMs } from '../lib/uptimeUtils';
import { useLang } from '../i18n';

export function SyncBar({ loading, error, lastFetch, onRefresh, openCount, updateSource, pollInterval, setPollInterval, autoMode = true, onAutoPoll = null, lanOnline = false, netOnline = true }) {
  const { t } = useLang();
  const [tick, setTick] = useState(0);
  const secAgo = lastFetch ? Math.floor((Date.now() - lastFetch.getTime()) / 1000) : null;
  // Adaptive cadence: per-second while the fetch is fresh, then back off — an
  // always-on screen shouldn't re-render every second forever. Self-rescheduling
  // timeout (re-runs as `tick`/`secAgo` change) instead of a permanent interval.
  useEffect(() => {
    const id = setTimeout(() => setTick(v => v + 1), syncTickMs(secAgo));
    return () => clearTimeout(id);
  }, [tick, secAgo]);
  const isLive = secAgo != null && secAgo < 4;
  const paused = pollInterval === 0;
  const storeOpen = isStoreOpen();

  const sourceTag = updateSource === 'webhook'
    ? <span className="kds__src-tag kds__src-tag--webhook">⚡ webhook</span>
    : updateSource === 'poll'
      ? <span className="kds__src-tag kds__src-tag--poll">↻ poll</span>
      : null;

  return (
    <div className={`kds__sync${error ? ' kds__sync--err' : ''}`}>
      <div className="kds__sync-left">
        <span className={`kds__dot${error ? ' kds__dot--err' : paused ? ' kds__dot--paused' : isLive ? ' kds__dot--live' : ''}`} />
        <span className="kds__sync-txt">
          {error
            ? t('sync.error', { error })
            : loading && !lastFetch
              ? t('sync.connecting')
              : <>
                  {t('sync.openOrders', { n: openCount })}
                  {' · '}{secAgo != null ? t('sync.secAgo', { n: secAgo }) : '—'}
                  {!paused && <>{' · '}{sourceTag}</>}
                  {paused
                    ? <span className="kds__sync-paused"> · ⏸ {t('sync.paused')}</span>
                    : <span className="kds__sync-hint"> · {t('sync.liveBackup', { n: pollInterval / 1000 })}</span>
                  }
                </>
          }
        </span>
      </div>
      <div className="kds__sync-right">
        <div className="kds__ar-wrap">
          <span className="kds__ar-label">
            {t('sync.autoRefresh')}
            <span className={`kds__store-pill${storeOpen ? ' kds__store-pill--open' : ' kds__store-pill--closed'}`}>
              {storeOpen ? t('sync.storeOpen') : t('sync.storeClosed')}
            </span>
          </span>
          <div className="kds__ar-options">
            {/* Auto = follow store hours. Any explicit choice is pinned and survives
                a reload, so a dedicated kitchen screen keeps polling after a reboot. */}
            {onAutoPoll && (
              <button
                className={`kds__ar-btn${autoMode ? ' kds__ar-btn--on' : ''}`}
                onClick={onAutoPoll} title={t('sync.autoTitle')}>
                {t('sync.auto')}
              </button>
            )}
            {AUTO_REFRESH_OPTIONS.map(o => (
              <button key={o.value}
                className={`kds__ar-btn${!autoMode && pollInterval === o.value ? ' kds__ar-btn--on' : ''}`}
                onClick={() => setPollInterval(o.value)}
                title={t('sync.pinTitle')}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <button className="kds__sync-btn" onClick={onRefresh} disabled={loading}>{t('sync.refresh')}</button>
        <span
          className={`kds__lan-pill${lanOnline ? ' kds__lan-pill--on' : ' kds__lan-pill--off'}`}
          title={lanOnline ? t('sync.lanOn') : t('sync.lanOff')}
        >
          {lanOnline ? '📡 LAN' : '📡 —'}
        </span>
        {!netOnline && <span className="kds__offline-badge">{t('sync.offline')}</span>}
      </div>
    </div>
  );
}
